import type { ReactNode } from "react";
import { memo, useCallback } from "react";

import cc from "classcat";
import { useLocalization } from "@fluent/react";
import { translateOAuthApplication } from "byzantine/src/l10n/SettingLocalizers";

import AppAuthorize, {
  defaultRenderButton,
} from "../../../../components/AppAuthorize";
import { requestNavigation } from "../../../utils/navigationGuard";

import styles from "./MenuItem.module.scss";

import type { NAFOption } from "../sidebar.types";

interface SidebarNAFItemProps {
  isVisible?: boolean;
  isActive?: boolean;
  nafOption: NAFOption;
  hideIcon?: boolean;
}

// Renders a NAF item in the sidebar, either a legacy link or an app authorization link.
const SidebarNAFItem = ({
  nafOption,
  isVisible,
  isActive = false,
  hideIcon = false,
}: SidebarNAFItemProps) => {
  const { l10n } = useLocalization();

  const subItemContainerClass = cc([
    styles["icon-title-container"],
    styles["menu-item-text"],
    !isActive && styles.hover,
    isActive && styles["sub-item-active"],
  ]);

  const tabIndex = isVisible ? 0 : -1;

  const handleAppClick = useCallback(
    (handleClick: () => void, children: ReactNode, dataClassnames?: string) =>
      defaultRenderButton(
        handleClick,
        children,
        "",
        dataClassnames,
        tabIndex,
        "menuitem"
      ),
    [tabIndex]
  );

  if (nafOption?.discriminator === "legacy-naf") {
    const handleClick = () => {
      if (nafOption.open_new_tab) {
        window.open(nafOption.url, "_blank");
      } else {
        requestNavigation(() => window.open(nafOption.url, "_self"));
      }
    };

    return (
      <button
        key={nafOption.title}
        className={subItemContainerClass}
        title={nafOption.title}
        onClick={handleClick}
        role="menuitem"
        tabIndex={tabIndex}
      >
        {!hideIcon && nafOption.icon && (
          <div className={`narmi-icon-${nafOption.icon}`} aria-hidden="true" />
        )}
        <span>{nafOption.title}</span>
      </button>
    );
  }

  if (nafOption?.discriminator === "app") {
    const translatedAppName = (() => {
      const localizedApp = translateOAuthApplication(
        {
          uuid: nafOption.app.id,
          name: nafOption.app.name,
          description: nafOption.app.description,
        },
        l10n
      );
      return localizedApp.name;
    })();

    return (
      <AppAuthorize
        app={nafOption.app}
        key={`app_authorize_${nafOption.app.name}`}
        renderButton={handleAppClick}
      >
        <div className={subItemContainerClass} title={translatedAppName}>
          {!hideIcon && (
            <div
              className={`narmi-icon-${nafOption.app.image}`}
              aria-hidden="true"
            />
          )}
          <span>{translatedAppName}</span>
        </div>
      </AppAuthorize>
    );
  }

  return null;
};

export default memo(SidebarNAFItem);
